/*
 * Copyright (c) 2024 AccelByte Inc. All Rights Reserved
 * This is licensed software from AccelByte Inc, for limitations
 * and restrictions contact your company contract manager.
 */

import { ExtensionProjectResponse } from '@cp/customer-database-sdk'
import { BaseUserHelpers } from '../BaseUserHelpers'
import { EngineHelpers } from '../project/EngineHelpers'
import { ProjectNameHelpers } from '../project/ProjectNameHelpers'
import { DEFAULT_RENDER_FUNCTION, getColumnValue } from './TableColumnHelpers'
import { ColumnDefinitionFn, ColumnRendererRecord, ValidProjectColumn } from './types'

const COLUMN_TITLE_MAPPING = {
  name: 'Project Name',
  'organization.name': 'Organization Name',
  engine: 'Engine',
  platforms: 'Platforms',
  'organization.deliveryManager.primary': 'Primary Delivery Manager'
} satisfies Record<ValidProjectColumn, string>

const DEFAULT_COLUMN_RENDERER_MAPPING = {
  name: projectName => ProjectNameHelpers.renderName(projectName),
  'organization.name': undefined,
  'organization.deliveryManager.primary': primaryDM => BaseUserHelpers.renderFullName(primaryDM),
  engine: engine => EngineHelpers.getEngineName(engine),
  platforms: undefined
} satisfies ColumnRendererRecord<ExtensionProjectResponse, ValidProjectColumn>

const DEFAULT_COLUMNS_RECORD = [
  'name',
  'organization.name',
  'organization.deliveryManager.primary',
  'engine',
  'platforms'
] satisfies Array<ValidProjectColumn>

export class ProjectTableColumnDefinitionHelpers {
  static VALID_PROJECT_COLUMNS = Object.keys(DEFAULT_COLUMN_RENDERER_MAPPING) as Array<ValidProjectColumn>
  static DEFAULT_COLUMNS_RECORD = DEFAULT_COLUMNS_RECORD

  static getDefaultProjectColumnsTitleMapping = (columnsKey: Array<ValidProjectColumn> | undefined = DEFAULT_COLUMNS_RECORD) => {
    const columnTitle: string[] = []
    for (const key of columnsKey) {
      const mappedColumnTitleFromKey = COLUMN_TITLE_MAPPING[key]
      if (mappedColumnTitleFromKey) {
        columnTitle.push(mappedColumnTitleFromKey)
      }
    }

    return columnTitle
  }

  static getProjectColumn: ColumnDefinitionFn<ExtensionProjectResponse, ValidProjectColumn> = (param, renderFn) => ({
    name: param,
    title: COLUMN_TITLE_MAPPING[param],
    render: record => {
      const effectiveRenderFn = renderFn ?? (DEFAULT_COLUMN_RENDERER_MAPPING[param] as typeof renderFn) ?? DEFAULT_RENDER_FUNCTION
      const effectiveValue = getColumnValue(record, param)
      return effectiveRenderFn(effectiveValue, record)
    }
  })
}
